/**
 * 261104 E1-P1 — kiểu dữ liệu «Chuyển thể mới» (theo skill). CHỈ KIỂU (không import gì) để util thuần/jest dùng được.
 * Khớp bản chiếu `GET /api/v1/admin/film/skill-adaptation/runs/:runId` của admin-service (`skillProjection.ts`).
 */
export type SkillGate = 'ENDING' | 'CHANGES' | 'SPINE' | 'EPISODES';
export const SKILL_GATES: readonly SkillGate[] = ['ENDING', 'CHANGES', 'SPINE', 'EPISODES'];
export type SkillRunStatus = 'DRAFT' | 'RUNNING' | 'WAIT_GATE' | 'PAUSED' | 'FAILED' | 'GATES_DONE' | 'WRITING' | 'PUSHING' | 'NEEDS_DECISION' | 'DONE';
export type SkillLocalizeMode = 'OFF' | 'KEEP' | 'MAP';
export type SkillOutputLanguage = 'vi' | 'en';
export type SkillChangeChoice = 'APPLY' | 'SKIP';
export type SkillPauseReason = 'USER' | 'BUDGET';

export interface SkillProgress { label: string; next: string | null; done: number | null; total: number | null; updatedAt: string }
export interface SkillGateDocument {
  revision: number; text: string; data: unknown; origin: 'MACHINE' | 'REWRITE' | 'REGENERATE'; instruction: string | null; basedOnRevision: number | null; createdAt: string;
}
export interface SkillGateView { current: SkillGateDocument | null; revisions: Array<{ revision: number; origin: string; createdAt: string }> }
export interface SkillDecision { gate: SkillGate; documentRevision: number; choice: unknown; at: string; by: string | null }
export interface SkillRunInputView {
  fileName: string; chapterCount: number; totalChars: number;
  suspectRanges: Array<{ from: number; to: number; count: number }>; unreadable: number[];
  range: { from: number; to: number }; excludedChapters: number[];
  outputLanguage: SkillOutputLanguage; localize: { mode: SkillLocalizeMode; targetCulture: string | null };
  instructions: string | null; limits: { maxEpisodeSeconds: number; targetSeconds: [number, number] };
}
/** E1-P2: chặng viết / ghi — khớp `WritingView` của `skillProjection.ts`. Bản sạch KHÔNG nằm ở đây (GET episodes/:n). */
export type SkillEpisodePhase = 'PENDING' | 'WRITING' | 'REVIEWING' | 'DONE' | 'BOUNDARY';
export type SkillBoundaryChoice = 'KEEP' | 'RECOMMENDED';
export interface SkillBoundaryView {
  reason: string; recommendation: string; affectedEpisodes: number[]; resolution: 'PENDING' | 'RECOMMENDED' | 'KEPT';
  canRecommend: boolean; recommendDisabledReason: string | null; estimateUsd: number;
}
export interface SkillWritingEpisode {
  n: number; title: string; phase: SkillEpisodePhase; boundaryGeneration: number;
  estLowSec: number | null; estHighSec: number | null; scriptChars: number | null;
  reviewed: boolean; overlong: boolean; overlongNote: string | null; limitNote: string | null;
  boundary: SkillBoundaryView | null;
  handoff: { chapterId: string; result: string; at: string; acceptedBy: string | null } | null;
  /** `hasPlan`: chương đang được một Film Plan dùng (BE tra) ⇒ `kind` đã là CONFLICT_PLAN và `canReplace=false` ngay ở danh sách. Vắng ở BE cũ ⇒ coi như false. */
  conflict: { kind: string; chapterId: string | null; keptBy: string | null; hasPlan?: boolean; canReplace: boolean } | null;
}
export interface SkillWritingView {
  origin: 'GATES' | 'LEGACY_GATES_DONE'; total: number; doneCount: number; reconciledCount: number; pushUpTo: number | null; finalSkipped: boolean;
  hasFinalState: boolean; finalStateSummary: { endBeat: string; characters: number; openThreads: number } | null;
  episodes: SkillWritingEpisode[];
  stopEarly: null | { kind: 'PREVIOUS' | 'SKIP_FINAL'; upTo: number | null };
}
export interface SkillEpisodeDetail {
  n: number; title: string; phase?: SkillEpisodePhase; scriptText: string; scriptChars?: number | null; estLowSec: number | null; estHighSec: number | null; basis?: string | null;
  overlongNote: string | null; limitNote: string | null; selfCheck?: unknown; sourceChapters?: number[];
}
export interface SkillComparison {
  episode: number; title: string; mine: { content: string };
  existing: { chapterId: string; name: string | null; orderIndex: number; content: string; contentSha256: string; modifiedAt: string; hasPlan: boolean } | null;
  kind: string | null; canReplace: boolean;
}
export interface SkillConflictDecision { n: number; choice: 'KEEP' | 'REPLACE'; expectedContentSha256?: string; expectedModifiedAt?: string }
export interface SkillConflictReplaceAll { n: number; expectedContentSha256: string; expectedModifiedAt: string }
export interface SkillFinalState { text: string; data: unknown; lastEpisodeN: number; at: string }

export interface SkillRunProjection {
  /** E1-P2: tóm tắt chặng viết / ghi; null ở lượt chưa tới đó. */
  writing?: SkillWritingView | null;
  id: string; storyId: string; status: SkillRunStatus; gate: SkillGate | null; flowVersion: string; skillVersion: string;
  displayState: string; needsMoreBudget: boolean; progress: SkillProgress | null;
  input: SkillRunInputView;
  documents: Record<SkillGate, SkillGateView>;
  decisions: SkillDecision[];
  approvedGates: SkillGate[];
  overlongEpisodes: number[];
  coverage: { limitSentence: string | null; missing: Array<{ n: number; reason: string }>; indexedCount: number; retrieval: { loadedCount: number; notLoadedCount: number } | null } | null;
  pauseRequested: boolean;
  /** R2: một đơn vị đã hết 3 thế hệ × 3 lần gửi — nút tiếp tục mờ, câu giải thích ở `lastError.message`. */
  exhausted: boolean;
  /** N1: kế hoạch chia cụm của khoảng hiện tại (chỉ khi DRAFT). `ok=false` ⇒ khoảng quá lớn; `suggestedChapters` = số chương đầu vừa trần. */
  sourcePlan: { ok: boolean; clusterCount: number; suggestedChapters: number | null; message: string | null } | null;
  canStart: boolean;
  suggestedRange: { from: number; to: number } | null;
  pending: { kind: string; state: 'RUNNING' | 'FAILED' | 'PAUSED'; gate: SkillGate | null; pauseReason: SkillPauseReason | null; instruction: string | null } | null;
  lastError: { code: string; message: string; unitKey: string | null; retryable: boolean; at: string } | null;
  budget: { spentUsd: number; reservedUsd: number; capUsd: number; blocked: boolean };
  createdAt: string; modifiedAt: string;
}
export interface SkillRunSummary {
  id: string; status: SkillRunStatus; gate: SkillGate | null; displayState: string; fileName: string; range: { from: number; to: number }; createdAt: string; modifiedAt: string;
}
export interface SkillInputPatch {
  range?: { from: number; to: number }; excludedChapters?: number[]; outputLanguage?: SkillOutputLanguage;
  localize?: { mode: SkillLocalizeMode; targetCulture: string | null }; instructions?: string | null;
}

/** Dữ liệu `data` của từng cổng (phần FE hiển thị). Trường nào có thể vắng ⇒ để tuỳ chọn, FE phải chịu được. */
export interface EndingOptionData {
  id: string; title: string; recommended?: boolean; summary: string;
  endingMilestone?: { id?: string; shortName?: string; chapter?: number };
  basisChapters?: { ending?: number[]; setup?: number[]; aftermath?: number[] };
  keep?: string; scale?: string; fixCost?: string; tradeoff?: string;
}
export interface EndingDataView { coverageSentence?: string; promise?: string; options: EndingOptionData[]; comparison?: { criteria?: string[]; rows?: Array<{ optionId: string; values?: string[] }> } }
export type SkillChangeGroup = 'RELATIONSHIP' | 'ANTAGONIST_FINALE' | 'LOCALIZATION';
export type SkillChangeKind = 'SOURCE' | 'MERGED' | 'NEW';
export interface ChangeItemData {
  id: string; group: SkillChangeGroup; kind: SkillChangeKind; title: string;
  problem?: string; proposal?: string; benefit?: string; affectedMilestones?: string; cost?: string;
}
export interface ChangesDataView {
  changes: ChangeItemData[]; keepGroups?: Array<{ group: SkillChangeGroup; reason: string }>;
  draftChoices?: Record<string, SkillChangeChoice>; newChangeIds?: string[];
  localizeTable?: Array<{ kind: 'NAME' | 'PLACE' | 'TERM' | 'WORLD'; source: string; target: string; note?: string }>; localizeMode?: SkillLocalizeMode;
  /** v2 (E1-P2) — tuỳ chọn: lượt cũ không có ⇒ FE rơi về bảng P1. */
  relationshipMap?: RelationshipRowData[]; relationshipAssessment?: string; antagonistMap?: AntagonistMapData;
}
export interface RelationshipRowData { pair: string; role?: string; arc?: string; stages?: string[]; commitmentKnowledge?: string }
export interface AntagonistMapData {
  goal?: string; resistance?: string; decisiveChoice?: string; winMechanism?: string; setupsNeeded?: string[]; consequence?: string; openParts?: string;
}
export interface MilestoneData { id: string; shortName: string; label: SkillChangeKind; sourceChapters?: number[]; summary?: string; clusterId?: string; consequence?: string; weight?: string }
export interface SpineClusterData { id: string; name: string; range?: string }
export interface SpineDataView { milestones: MilestoneData[]; clusters?: SpineClusterData[] }
export interface EpisodeData { n: number; title?: string; scenes?: string; sourceChapters?: number[]; estLowSec?: number; estHighSec: number; basis?: string }
export interface EpisodesDataView { episodes: EpisodeData[] }
